/**
 * Triggers the newsletter popup dialog after a merchant-configured delay or scroll depth,
 * and suppresses it for a configurable number of days after the shopper dismisses it or
 * submits the form. Dismissal state is device-local (localStorage) — there is no
 * account-level or cross-device suppression in this phase.
 */
const STORAGE_KEY = 'bh:newsletter-popup-dismissed-until';

class NewsletterPopupTrigger extends HTMLElement {
  connectedCallback() {
    if (this.dataset.trigger === 'disabled') return;
    if (this.#isSuppressed()) return;

    const dialog = this.querySelector('dialog-component');
    if (!dialog || typeof dialog.showDialog !== 'function') return;

    dialog.addEventListener('dialog:close', this.#suppress, { once: true });
    this.addEventListener('submit', this.#suppress, { capture: true });

    if (this.dataset.trigger === 'scroll') {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#onScroll();
    } else {
      const delaySeconds = Number.parseInt(this.dataset.delaySeconds ?? '', 10) || 12;
      this.#timer = window.setTimeout(() => dialog.showDialog(), delaySeconds * 1000);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.clearTimeout(this.#timer);
  }

  /** @type {number | undefined} */
  #timer;

  #onScroll = () => {
    const targetPercentage = Number.parseInt(this.dataset.scrollPercentage ?? '', 10) || 50;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const scrolledPercentage = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;

    if (scrolledPercentage < targetPercentage) return;

    window.removeEventListener('scroll', this.#onScroll);
    this.querySelector('dialog-component')?.showDialog();
  };

  #suppress = () => {
    const days = Number.parseInt(this.dataset.dismissDays ?? '', 10) || 14;
    const suppressUntil = Date.now() + days * 24 * 60 * 60 * 1000;

    try {
      localStorage.setItem(STORAGE_KEY, String(suppressUntil));
    } catch {
      // Storage unavailable (e.g. private browsing) — the popup may show again next visit.
    }
  };

  #isSuppressed() {
    try {
      const suppressUntil = Number.parseInt(localStorage.getItem(STORAGE_KEY) ?? '', 10);
      return Number.isFinite(suppressUntil) && Date.now() < suppressUntil;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('newsletter-popup-trigger')) {
  customElements.define('newsletter-popup-trigger', NewsletterPopupTrigger);
}
